import { supabase } from './supabase'
// import { directAPI } from './supabase-direct' // Unused
import { insertContributionDirect } from './contributions-simple'

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL

// Extract a valid {width, height} pair (both positive integers) from an image-like object,
// or undefined. Used to carry intrinsic dimensions through the cache/insert pipeline without
// ever writing 0/negative/partial values.
function pickDims(o: any): { width: number; height: number } | undefined {
  const w = o?.width
  const h = o?.height
  if (typeof w === 'number' && typeof h === 'number' &&
      Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
    return { width: w, height: h }
  }
  return undefined
}

// Server-side image caching via Edge Function (bypasses CORS)
async function cacheImagesServerSide(
  images: Array<{ url: string; order?: number; selected?: boolean; highlighted?: boolean; alt?: string; cached_url?: string; width?: number; height?: number }>,
  tempId: string
): Promise<Array<{ url: string; order?: number; selected?: boolean; highlighted?: boolean; alt?: string; cached_url?: string; width?: number; height?: number }>> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) return images

  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/cache-product-images`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        images: images.map((img, i) => ({ url: img.url, order: img.order ?? i })),
        temp_id: tempId
      })
    })

    const result = await response.json()

    if (result.success && result.images) {
      return images.map(img => {
        const cached = result.images.find((r: any) => r.url === img.url)
        // Prefer freshly-measured dims from the edge function; fall back to any dims the image
        // already carried (e.g. Shopify's claimed dims). Omit both keys when neither is valid.
        const dims = pickDims(cached) ?? pickDims(img)
        return { ...img, cached_url: cached?.cached_url ?? img.url, ...(dims ?? {}) }
      })
    }
  } catch (error) {
    console.error('Server-side image caching failed:', error)
  }

  return images // Fall back to original URLs on any failure
}

export interface Contribution {
  id?: string
  user_id?: string
  title: string
  brand: string
  category: string
  description: string
  image_url?: string
  cached_image_url?: string // CDN-cached version
  images?: Array<{
    url: string
    cached_url?: string
    alt?: string
    order: number
    highlighted?: boolean
    selected?: boolean
    width?: number // intrinsic pixel width of the file at cached_url (omitted when unknown)
    height?: number // intrinsic pixel height; always paired with width, never partial
  }>
  impact?: string
  tags: string[]
  source_url?: string
  price_amount?: number | null
  price_currency?: string | null
  comment?: string // User's personal comment about why they love this product
  saves_count?: number
  views_count?: number
  is_featured?: boolean
  is_active?: boolean
  is_hidden?: boolean // admin shadow-hide: RLS strips the row for everyone but the owner and admins
  hidden_reason?: string | null
  created_at?: string
  updated_at?: string
  // Joined data
  user?: {
    id: string
    email: string
    first_name: string
    last_name: string
    username?: string
    avatar_url?: string
  }
  classification?: {
    category_id: string
    subcategory_id: string | null
    categories: {
      id: string
      name: string
    }
    subcategories?: {
      id: string
      name: string
    } | null
  } | null
  // Convenience fields (flattened from classification and tags)
  category_id?: string
  subcategory_id?: string | null
  category_name?: string
  subcategory_name?: string | null
  tag_names?: string[]
  tag_ids?: string[]
  is_saved?: boolean
}

export interface Collection {
  id?: string
  user_id?: string
  name: string
  description?: string
  is_public?: boolean
  cover_image_url?: string
  created_at?: string
  updated_at?: string
}

export const contributionsService = {
  // Expose supabase for direct queries
  supabase,

  // Fetch all contributions with user data
  async getAllContributions() {
    try {
      console.log('Fetching all contributions...')

      // Fetch contributions with comment counts
      const { data: contribsOnly, error: contribError } = await supabase
        .from('contributions')
        .select('*, comments(count)')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
      
      console.log('Contributions without user join:', contribsOnly)
      if (contribsOnly && contribsOnly.length > 0) {
        console.log('First contribution images:', contribsOnly[0].images)
      }
      
      if (contribError) {
        console.error('Error fetching contributions:', contribError)
        throw contribError
      }
      
      // If we have contributions, try to fetch with user data
      // but fall back to contributions without user data if it fails
      try {
        const { data, error } = await supabase
          .from('contributions')
          .select(`
            *,
            user:users!contributions_user_id_fkey (
              id,
              email,
              first_name,
              last_name,
              username,
              avatar_url
            ),
            classification:item_classifications (
              category_id,
              subcategory_id,
              categories (
                id,
                name
              ),
              subcategories (
                id,
                name
              )
            ),
            item_tags (
              tags (
                id,
                name
              )
            ),
            comments(count)
          `)
          .eq('is_active', true)
          .order('created_at', { ascending: false })

        if (error) {
          console.warn('Error fetching with user data, using contributions without user data:', error)
          // Parse comment counts from fallback data
          const parsedContribsOnly = (contribsOnly || []).map((contrib: any) => ({
            ...contrib,
            comments_count: contrib.comments?.[0]?.count || 0
          }))
          return parsedContribsOnly
        }

        console.log('Contributions with user data:', data)
        // Parse comment counts from Supabase's format: [{ count: 1 }] -> 1
        const parsedData = (data || []).map((contrib: any) => ({
          ...contrib,
          comments_count: contrib.comments?.[0]?.count || 0
        }))
        return parsedData
      } catch (joinError) {
        console.warn('Join failed, returning contributions without user data:', joinError)
        // Parse comment counts from fallback data
        const parsedContribsOnly = (contribsOnly || []).map((contrib: any) => ({
          ...contrib,
          comments_count: contrib.comments?.[0]?.count || 0
        }))
        return parsedContribsOnly
      }
    } catch (error) {
      console.error('Error fetching contributions:', error)
      return [] // Return empty array instead of throwing
    }
  },

  // Fetch contributions with pagination support
  async getContributionsPaginated(offset: number = 0, limit: number = 30) {
    try {
      console.log(`Fetching contributions: offset=${offset}, limit=${limit}`)

      // First, get total count for pagination. Hidden items are excluded
      // from the discover feed for EVERYONE — including admins and the
      // owner, whom RLS would otherwise let through (PUR-19).
      const { count, error: countError } = await supabase
        .from('contributions')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true)
        .eq('is_hidden', false)

      if (countError) {
        console.error('Error getting count:', countError)
      }

      // Fetch paginated contributions with all relationships
      const { data, error } = await supabase
        .from('contributions')
        .select(`
          *,
          user:users!contributions_user_id_fkey (
            id,
            email,
            first_name,
            last_name,
            username,
            avatar_url
          ),
          classification:item_classifications (
            category_id,
            subcategory_id,
            categories (
              id,
              name
            ),
            subcategories (
              id,
              name
            )
          ),
          item_tags (
            tags (
              id,
              name
            )
          ),
          comments(count)
        `)
        .eq('is_active', true)
        .eq('is_hidden', false)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1)

      if (error) {
        console.error('Error fetching paginated contributions:', error)
        throw error
      }

      // Parse comment counts from Supabase's format: [{ count: 1 }] -> 1
      const parsedData = (data || []).map((contrib: any) => ({
        ...contrib,
        comments_count: contrib.comments?.[0]?.count || 0
      }))

      return {
        data: parsedData,
        count: count || 0,
        hasMore: count ? offset + limit < count : false
      }
    } catch (error) {
      console.error('Error fetching paginated contributions:', error)
      return {
        data: [],
        count: 0,
        hasMore: false
      }
    }
  },

  // Fetch user's own contributions
  async getUserContributions(userId: string) {
    try {
      const { data, error } = await supabase
        .from('contributions')
        .select(`
          *,
          user:users!contributions_user_id_fkey (
            id,
            email,
            first_name,
            last_name,
            username,
            avatar_url
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error fetching user contributions:', error)
      throw error
    }
  },

  // Create a new contribution
  async createContribution(contribution: Omit<Contribution, 'id' | 'user_id' | 'created_at' | 'updated_at'>, userId?: string, accessToken?: string) {
    try {
      // Require userId to be passed to avoid auth issues
      if (!userId) {
        throw new Error('User ID is required to create contribution')
      }

      if (!accessToken) {
        throw new Error('Access token is required to create contribution')
      }

      console.log('Inserting contribution for user:', userId)
      console.log('Contribution data to insert:', contribution)
      console.log('Have access token:', !!accessToken)

      // Normalize brand name and get brand_id
      let brand = contribution.brand
      let brandId = null
      if (brand && brand.trim().length > 0) {
        try {
          const { data: brandData, error: brandError } = await supabase.functions.invoke('normalize-brand-name', {
            body: { brand }
          })
          if (!brandError && brandData?.brand_name) {
            brand = brandData.brand_name
            brandId = brandData.brand_id
            console.log(`Brand normalized: "${contribution.brand}" -> "${brand}" (${brandId})`)
          }
        } catch (error) {
          console.error('Brand normalization failed, using original:', error)
        }
      }

      // Fix category if it's "Tech" to "Technology"
      let category = contribution.category
      if (category === 'Tech') {
        category = 'Technology'
      }

      // Generate a temporary contribution ID for image organization
      const tempContributionId = `temp_${userId}_${Date.now()}`

      // Cache images server-side (bypasses CORS issues)
      let cachedImageUrl = contribution.cached_image_url
      let cachedImages = (contribution.images || []).map((img, index) => ({
        ...img,
        order: img.order ?? index
      }))

      // Build list of all images to cache (primary + additional)
      const allImagesTocache: Array<{ url: string; order: number; selected?: boolean; highlighted?: boolean }> = []

      if (!cachedImageUrl && contribution.image_url) {
        allImagesTocache.push({ url: contribution.image_url, order: -1, highlighted: true })
      }

      const needsCaching = cachedImages.some((img: any) => !img.cached_url)
      if (needsCaching) {
        allImagesTocache.push(...cachedImages.filter((img: any) => !img.cached_url))
      }

      // Cache all images in one server-side call
      if (allImagesTocache.length > 0) {
        const cachedResults = await cacheImagesServerSide(allImagesTocache, tempContributionId)

        // Extract primary image result
        const primaryResult = cachedResults.find(r => r.order === -1)
        if (primaryResult) {
          cachedImageUrl = primaryResult.cached_url || contribution.image_url
        }

        // Update additional images with cached URLs
        cachedImages = cachedImages.map(img => {
          const cached = cachedResults.find(r => r.url === img.url)
          const dims = pickDims(cached) ?? pickDims(img)
          return { ...img, cached_url: cached?.cached_url ?? img.url, ...(dims ?? {}) }
        })
      }
      
      const insertData = {
        ...contribution,
        brand, // Use normalized brand name
        brand_id: brandId, // Add brand_id foreign key
        category,
        user_id: userId,
        image_url: contribution.image_url || null, // Keep original for reference
        cached_image_url: cachedImageUrl || null, // Use cached version
        images: cachedImages, // Use cached images array
        source_url: contribution.source_url || null,
        impact: contribution.impact || null,
        tags: contribution.tags || [],
        comment: contribution.comment || null,
        is_active: true, // Explicitly set is_active to true
        saves_count: 0,
        views_count: 0
      }
      
      
      // Skip Supabase client entirely - use direct fetch with proper auth
      const result = await insertContributionDirect(insertData, userId, accessToken)
      return result

    } catch (error) {
      console.error('Error creating contribution:', error)
      throw error
    }
  },

  // Update a contribution
  async updateContribution(id: string, updates: Partial<Contribution>) {
    try {
      console.log('Updating contribution:', { id, updates })
      console.log('Update keys:', Object.keys(updates))
      console.log('Update values:', Object.values(updates))

      // First check if the contribution exists
      const { error: checkError } = await supabase
        .from('contributions')
        .select('id')
        .eq('id', id)
        .single()

      if (checkError) {
        console.error('Contribution not found or access denied:', checkError)
        throw new Error('Contribution not found or you do not have permission to edit it')
      }

      // Cache images if they're being updated (server-side to bypass CORS)
      const updatesToSave = { ...updates }

      // Build list of images to cache
      const imagesToCache: Array<{ url: string; order: number }> = []

      if (updates.image_url && !updates.cached_image_url) {
        imagesToCache.push({ url: updates.image_url, order: -1 })
      }

      if (updates.images && Array.isArray(updates.images)) {
        const needsCaching = updates.images.some((img: any) => img.url && !img.cached_url)
        if (needsCaching) {
          imagesToCache.push(...updates.images
            .filter((img: any) => img.url && !img.cached_url)
            .map((img: any, index: number) => ({ url: img.url, order: img.order ?? index }))
          )
        }
      }

      // Cache all images in one server-side call
      if (imagesToCache.length > 0) {
        const cachedResults = await cacheImagesServerSide(imagesToCache, id)

        // Extract primary image result
        const primaryResult = cachedResults.find(r => r.order === -1)
        if (primaryResult) {
          updatesToSave.cached_image_url = primaryResult.cached_url || updates.image_url
          console.log(`Cached primary image: ${updates.image_url} -> ${updatesToSave.cached_image_url}`)
        }

        // Update additional images with cached URLs - preserve all properties including highlighted and selected
        if (updates.images && Array.isArray(updates.images)) {
          updatesToSave.images = updates.images.map((img: any, index: number) => {
            const cached = cachedResults.find(r => r.url === img.url)
            const dims = pickDims(cached) ?? pickDims(img)
            return {
              ...img, // Preserve all properties (highlighted, selected, etc.)
              cached_url: cached?.cached_url ?? img.cached_url ?? img.url,
              order: img.order ?? index,
              ...(dims ?? {})
            }
          })
          console.log(`Cached ${updatesToSave.images.length} images`)
        }
      }

      // Perform the update
      const { data, error } = await supabase
        .from('contributions')
        .update(updatesToSave)
        .eq('id', id)
        .select()
        .single()

      if (error) {
        console.error('Update error details:', error)
        console.error('Error code:', error.code)
        console.error('Error message:', error.message)
        console.error('Error hint:', error.hint)
        console.error('Error details:', error.details)
        if (error.message?.includes('coerce')) {
          throw new Error('Failed to update contribution. The item may have been deleted or you may not have permission.')
        }
        throw new Error(error.message || 'Failed to update contribution')
      }

      console.log('Update successful:', data)
      return data
    } catch (error: any) {
      console.error('Error updating contribution:', error)
      throw error
    }
  },

  // Delete a contribution
  async deleteContribution(id: string) {
    try {
      const { error } = await supabase
        .from('contributions')
        .delete()
        .eq('id', id)

      if (error) throw error
      return true
    } catch (error) {
      console.error('Error deleting contribution:', error)
      throw error
    }
  },

  // Check if user has saved contributions (via default curation)
  async checkUserSaves(contributionIds: string[]) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return []

      // Import dynamically to avoid circular dependency
      const { curationsService } = await import('./curations')
      const savedSet = await curationsService.checkUserSaves(user.id, contributionIds)
      return Array.from(savedSet)
    } catch (error) {
      console.error('Error checking user saves:', error)
      return []
    }
  },

  // Get contributions with save status for current user
  async getContributionsWithSaves() {
    try {
      console.log('Getting contributions with saves...')
      const contributions = await this.getAllContributions()

      if (!contributions || contributions.length === 0) {
        console.log('No contributions found')
        return []
      }

      console.log(`Found ${contributions.length} contributions`)

      const contributionIds = contributions.map((c: any) => c.id).filter(Boolean) as string[]
      const savedIds = await this.checkUserSaves(contributionIds)

      const result = contributions.map((contribution: any) => {
        // Extract tag names from item_tags relationship
        const tagNames = contribution.item_tags?.map((it: any) => it.tags?.name).filter(Boolean) || []

        return {
          ...contribution,
          is_saved: savedIds.includes(contribution.id!),
          // Flatten classification data for easier access (IDs and names)
          category_id: contribution.classification?.category_id || null,
          subcategory_id: contribution.classification?.subcategory_id || null,
          category_name: contribution.classification?.categories?.name || contribution.category,
          subcategory_name: contribution.classification?.subcategories?.name || null,
          // Flatten tags for easier access (use new tags, fallback to old tags field)
          tag_names: tagNames.length > 0 ? tagNames : (contribution.tags || [])
        }
      })

      return result
    } catch (error) {
      console.error('Error fetching contributions with saves:', error)
      return [] // Return empty array instead of throwing
    }
  },

  // Get contributions with save status for current user (paginated)
  async getContributionsWithSavesPaginated(offset: number = 0, limit: number = 30) {
    try {
      console.log(`Getting paginated contributions with saves: offset=${offset}, limit=${limit}`)
      const result = await this.getContributionsPaginated(offset, limit)

      if (!result.data || result.data.length === 0) {
        console.log('No contributions found')
        return {
          data: [],
          count: 0,
          hasMore: false
        }
      }

      console.log(`Found ${result.data.length} contributions`)

      const contributionIds = result.data.map((c: any) => c.id).filter(Boolean) as string[]
      const savedIds = await this.checkUserSaves(contributionIds)

      const contributions = result.data.map((contribution: any) => {
        // Extract tag names from item_tags relationship
        const tagNames = contribution.item_tags?.map((it: any) => it.tags?.name).filter(Boolean) || []

        return {
          ...contribution,
          is_saved: savedIds.includes(contribution.id!),
          // Flatten classification data for easier access (IDs and names)
          category_id: contribution.classification?.category_id || null,
          subcategory_id: contribution.classification?.subcategory_id || null,
          category_name: contribution.classification?.categories?.name || contribution.category,
          subcategory_name: contribution.classification?.subcategories?.name || null,
          // Flatten tags for easier access (use new tags, fallback to old tags field)
          tag_names: tagNames.length > 0 ? tagNames : (contribution.tags || [])
        }
      })

      return {
        data: contributions,
        count: result.count,
        hasMore: result.hasMore
      }
    } catch (error) {
      console.error('Error fetching paginated contributions with saves:', error)
      return {
        data: [],
        count: 0,
        hasMore: false
      }
    }
  },

  // Search contributions
  async searchContributions(searchTerm: string, category?: string, tags?: string[]) {
    try {
      let query = supabase
        .from('contributions')
        .select(`
          *,
          user:users!contributions_user_id_fkey (
            id,
            email,
            first_name,
            last_name,
            username,
            avatar_url
          )
        `)
        .eq('is_active', true)

      // Add search filter
      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,brand.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
      }

      // Add category filter
      if (category && category !== 'all') {
        query = query.eq('category', category)
      }

      // Add tags filter
      if (tags && tags.length > 0) {
        query = query.contains('tags', tags)
      }

      const { data, error } = await query.order('created_at', { ascending: false })

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error searching contributions:', error)
      throw error
    }
  },

  // Subscribe to real-time contribution updates
  subscribeToContributions(callback: (payload: any) => void) {
    return supabase
      .channel('contributions')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'contributions' 
      }, callback)
      .subscribe()
  },

  // Get user statistics
  async getUserStats(userId: string) {
    try {
      const { data, error } = await supabase
        .from('user_statistics')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (error && error.code !== 'PGRST116') throw error // Ignore not found error
      return data
    } catch (error) {
      console.error('Error fetching user statistics:', error)
      return null
    }
  }
}